import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Box, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLinkedServices,
  fetchLightweightUsersV4,
  selectLinkedServicesStatus,
  selectLightweightUsersV4,
  selectLightweightUsersV4Status,
} from 'src/v3/manage/manage.slice'
import {
  fetchComplianceRequirementDetail,
  fetchComplianceRequirements,
  fetchComplianceProgress,
  selectComplianceRequirementDetail,
  selectComplianceRequirementDetailStatus,
  selectComplianceProgress,
  selectComplianceProgressStatus,
} from 'src/v3/manage/compliance.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { useNotifications } from '../hooks/use-notifications'
import { COMPLIANCE_URLS } from '../constants'
import { deleteComplianceRequirement } from '../utils/api'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import EditDeleteButtons from '../components/edit-delete-buttons'
import ConfirmationModal from '../components/modals/confirmation-modal'
import ComplianceDetailsCard from './components/compliance-details-card'
// import ItemsProgressCard from './components/items-progress-card'
// import UsersProgressCard from './components/users-progress-card'
import RequirementsAccordionCard from './components/requirements-accordion-card'
import UsersAccordionCard from './components/users-accordion-card'

export default function ComplianceDetail() {
  useScrollToTop()

  let { id } = useParams<{ id: string }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let detail = useAppSelector(selectComplianceRequirementDetail(id!))
  let detailStatus = useAppSelector(
    selectComplianceRequirementDetailStatus(id!),
  )
  let progress = useAppSelector(selectComplianceProgress(id!))
  let progressStatus = useAppSelector(selectComplianceProgressStatus(id!))
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let allUsers = useAppSelector(selectLightweightUsersV4)
  let usersStatus = useAppSelector(selectLightweightUsersV4Status)
  let users = detail?.linkedServiceID
    ? (allUsers[detail.linkedServiceID] ?? [])
    : []

  let linkedServiceName = useLinkedServiceName(detail?.linkedServiceID)

  let [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  let [isDeleting, setIsDeleting] = useState(false)
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    await deleteComplianceRequirement(id)
      .then(() => {
        showSuccess('Compliance requirement deleted')
        if (detail?.linkedServiceID) {
          dispatch(fetchComplianceRequirements(detail.linkedServiceID))
        }
        navigate(COMPLIANCE_URLS.INDEX, {
          state: { linkedServiceID: detail?.linkedServiceID },
        })
      })
      .catch(() => showError('Failed to delete compliance requirement'))
      .finally(() => setIsDeleting(false))
  }

  let isLoading =
    detailStatus === 'pending' ||
    detailStatus === 'uninitialized' ||
    linkedServicesStatus === 'pending'

  let isProgressLoading =
    progressStatus === 'pending' || progressStatus === 'uninitialized'

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (!id) return
    if (!detailStatus || detailStatus === 'uninitialized') {
      dispatch(fetchComplianceRequirementDetail(id))
    }
  }, [id, detailStatus, dispatch])

  useEffect(() => {
    if (!id) return
    if (!progressStatus || progressStatus === 'uninitialized') {
      dispatch(fetchComplianceProgress(id))
    }
  }, [id, progressStatus, dispatch])

  useEffect(() => {
    if (
      detail?.linkedServiceID &&
      (!usersStatus[detail.linkedServiceID] ||
        usersStatus[detail.linkedServiceID] === 'uninitialized')
    ) {
      dispatch(fetchLightweightUsersV4(detail.linkedServiceID))
    }
  }, [detail?.linkedServiceID, usersStatus, dispatch])

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <PageTitleWithLinkedService
          pageTitle={detail?.name ?? ''}
          linkedServiceName={linkedServiceName}
          moduleName="Compliance"
          handleBackClick={() =>
            navigate(COMPLIANCE_URLS.INDEX, {
              state: { linkedServiceID: detail?.linkedServiceID },
            })
          }
          isLoading={isLoading}
        />
        <EditDeleteButtons
          moduleName="Compliance Requirement"
          canSubmit={true}
          isSubmitting={false}
          isDisabled={isLoading}
          handleEdit={() => id && navigate(COMPLIANCE_URLS.EDIT(id))}
          handleDelete={() => setIsDeleteModalOpen(true)}
        />
      </Box>

      <ComplianceDetailsCard
        detail={detail ?? null}
        progress={progress ?? null}
        isLoading={isLoading || isProgressLoading}
      />

      {/* <ItemsProgressCard
        progress={progress ?? null}
        isLoading={isProgressLoading}
      />

      <UsersProgressCard
        progress={progress ?? null}
        users={users ?? []}
        isLoading={isProgressLoading}
      /> */}

      <RequirementsAccordionCard
        complianceID={id!}
        linkedServiceID={detail?.linkedServiceID}
        progress={progress ?? null}
        users={users ?? []}
        isLoading={isProgressLoading}
      />

      <UsersAccordionCard
        complianceID={id!}
        linkedServiceID={detail?.linkedServiceID}
        progress={progress ?? null}
        users={users ?? []}
        isLoading={isProgressLoading}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        confirmText="Delete"
        confirmColor="error"
        isSubmitting={isDeleting}
      >
        <Typography>
          Are you sure you want to delete this compliance requirement? This
          action cannot be undone.
        </Typography>
      </ConfirmationModal>
      {NotificationComponent}
    </Container>
  )
}
